import {
  Image, Video, Type, Layout, Users, Mail,
  MessageSquareQuote, Minus, CalendarDays, ClipboardList,
  Globe, ImageIcon, MoveHorizontal, Grid3x3,
  ShoppingBag, Package, ShoppingCart, Table2,
  HelpCircle, MapPin, AlignJustify,
  Sparkles, Wrench, Phone, ShieldCheck, BarChart3, Star
} from 'lucide-react';

export const blockLabels = {
  hero: 'Hero Section', banner: 'Web Banner', products: 'Featured Products',
  about: 'About Section', gallery: 'Gallery', signup: 'Email Signup',
  custom_text: 'Custom Text', custom_image: 'Custom Image',
  video: 'Video Embed', testimonial: 'Testimonial', spacer: 'Spacer',
  booking: 'Booking Calendar', form: 'Custom Form',
  embed: 'Embed Link', single_image: 'Single Image',
  marquee: 'Marquee Ticker',
  fluid_canvas: 'Fluid Section',
  // Phase 2 NEW
  product_grid: 'Product Grid', product_detail: 'Product Detail (PDP)',
  cart_table: 'Cart Table', data_table: 'Pricing Table',
  // Phase 3 NEW
  faq: 'FAQ', contact: 'Contact Section', footer: 'Footer',
  // Iter 138 — Agency Long-Scroll
  agency_glass_stat:    'Glass Stat Hero',
  agency_service_cards: 'Service Cards Row',
  agency_cta_band:      'Emergency CTA Band',
  agency_why_grid:      'Why Us — 6 Grid',
  agency_stat_quad:     'Stat Quad Row',
  agency_review_badge:  'Inline Review Badge',
};

export const blockIcons = {
  hero: Video, banner: Image, products: Layout, about: Users,
  gallery: Image, signup: Mail, custom_text: Type, custom_image: Image,
  video: Video, testimonial: MessageSquareQuote, spacer: Minus,
  booking: CalendarDays, form: ClipboardList,
  embed: Globe, single_image: ImageIcon,
  marquee: MoveHorizontal,
  fluid_canvas: Grid3x3,
  // Phase 2 NEW
  product_grid: ShoppingBag, product_detail: Package,
  cart_table: ShoppingCart, data_table: Table2,
  // Phase 3 NEW
  faq: HelpCircle, contact: MapPin, footer: AlignJustify,
  // Iter 138
  agency_glass_stat:    Sparkles,
  agency_service_cards: Wrench,
  agency_cta_band:      Phone,
  agency_why_grid:      ShieldCheck,
  agency_stat_quad:     BarChart3,
  agency_review_badge:  Star,
};

export const BLOCK_TYPES = [
  { type: 'hero', label: 'Hero Section', desc: 'Full-screen hero with video/image' },
  { type: 'banner', label: 'Web Banner', desc: 'Image banner with overlay text' },
  { type: 'products', label: 'Products Grid', desc: 'Featured products display' },
  { type: 'about', label: 'About Section', desc: 'Brand story and image' },
  { type: 'gallery', label: 'Gallery', desc: 'Image gallery grid (up to 50 photos)' },
  { type: 'embed', label: 'Embed Link', desc: 'YouTube, Spotify, Vimeo, Maps, any URL' },
  { type: 'single_image', label: 'Single Image', desc: 'Standalone image with shape & alignment' },
  { type: 'donations', label: 'Donations', desc: 'Accept donations via card or PayPal' },
  { type: 'signup', label: 'Email Signup', desc: 'Newsletter form' },
  { type: 'custom_text', label: 'Custom Text', desc: 'Free-form text' },
  { type: 'custom_image', label: 'Custom Image', desc: 'Image with caption' },
  { type: 'video', label: 'Video Embed', desc: 'Upload or embed video' },
  { type: 'testimonial', label: 'Testimonial', desc: 'Customer quote' },
  { type: 'spacer', label: 'Spacer', desc: 'Visual spacing' },
  { type: 'booking', label: 'Booking Calendar', desc: 'Embed your booking page on site' },
  { type: 'form', label: 'Custom Form', desc: 'Create contact, quote, or inquiry forms' },
  { type: 'marquee', label: 'Marquee Ticker', desc: 'Moving lines of words — scroll text across the page' },
  { type: 'fluid_canvas', label: 'Fluid Section', desc: 'Free-positioning grid — drag blocks anywhere on a 24-column canvas' },
];

export const BLOCK_DEFAULTS = {
  hero: { video_url: '', image_url: '', focus_point: { x: 50, y: 50 }, title: '', tagline: '', buttons: [{ text: 'Shop Now', link: '#' }] },
  banner: { image_url: '', focus_point: { x: 50, y: 50 }, height: 'medium', text: '', buttons: [] },
  products: { title: 'Featured Products', subtitle: '', featured_product_ids: [] },
  about: { image_url: '', title: 'About Us', text: '', vision: '' },
  gallery: { title: 'Gallery', images: [], layout: 'grid' },
  signup: { title: 'Stay Updated', subtitle: '' },
  custom_text: { title: '', text: '', alignment: 'center' },
  custom_image: { image_url: '', caption: '' },
  video: { video_url: '', title: '', caption: '' },
  testimonial: { quote: '', author: '', role: '' },
  spacer: { height: 40 },
  booking: { title: 'Book an Appointment', show_prices: true, show_duration: true },
  donations: { title: 'Support Us', subtitle: '', amounts: [25, 50, 100] },
  form: { form_id: '', title: '', description: '', fields: [], header_image_url: '' },
  embed: { url: '', title: '', embed_type: '', source: '', embeddable: false, embed_url: '', thumbnail: '', layout: 'wide', buttons: [] },
  single_image: { image_url: '', caption: '', shape: 'rectangle', alignment: 'center', linked_url: '', buttons: [] },
  marquee: {
    words: ['Indigenous Made', 'Authentic', 'Handcrafted', 'Premium', 'Limited Edition'],
    speed: 'medium',
    direction: 'left',
    separator: 'dot',
    bg_color: '#0F172A',
    text_color: '#FFFFFF',
    font_size: 'lg',
    weight: 'bold',
    italic: false,
    height: 'medium',
  },
  // Phase 2 NEW — Storefront + Pricing blocks
  product_grid: {
    title: 'Best-Selling Products',
    tagline: '',
    products: [
      { name: 'Sample Product 1', price: '$29', category: 'Category' },
      { name: 'Sample Product 2', price: '$49', category: 'Category' },
      { name: 'Sample Product 3', price: '$69', category: 'Category' },
    ],
  },
  product_detail: {
    product: {
      name: 'Featured Product',
      price: '$199',
      category: 'Featured',
      description: 'Crafted with care. Edit this description in the panel.',
      sizes: ['S','M','L','XL'],
      swatches: ['#EF4444','#3B82F6','#10B981','#0E0E23'],
    },
  },
  cart_table: {
    title: 'Your Cart',
    items: [
      { name: 'Sample Item 1', price: 29.99, qty: 1 },
      { name: 'Sample Item 2', price: 49.99, qty: 2 },
    ],
  },
  data_table: {
    title: 'Pay as you go',
    tagline: 'Only pay for what you use.',
    columns: ['Tier', 'Usage', 'Price'],
    rows: [
      { tier: 'Free', usage: 'Up to 100 / mo', price: '$0' },
      { tier: 'Starter', usage: '100 – 1K / mo', price: '$9' },
      { tier: 'Growth', usage: '1K – 10K / mo', price: '$29' },
    ],
    aside_label: 'Easy Start',
    aside_text: 'Lock in your tier today, scale anytime.',
  },
  // Phase 3 NEW
  faq: {
    title: 'Frequently asked questions',
    items: [
      { question: 'How do I get started?', answer: 'Sign up free and explore — no credit card required.' },
      { question: 'What does it cost?', answer: 'Plans start at $0/month. Premium tiers unlock more features.' },
      { question: 'Can I cancel anytime?', answer: 'Yes. Cancel from your settings — no questions asked.' },
      { question: 'Do you offer support?', answer: 'Yes — email + live chat on all paid plans, community on free.' },
    ],
  },
  contact: {
    title: 'Get In Touch',
    tagline: '',
    fields: ['Name', 'Email', 'Subject', 'Message'],
    info: { address: '', phone: '', email: '' },
    buttons: [{ text: 'Send Message' }],
  },
  footer: {
    brand: 'Your Brand',
    tagline: 'Crafted with care.',
    columns: [
      { title: 'Product', links: ['Features', 'Pricing', 'Templates', 'Roadmap'] },
      { title: 'Company', links: ['About', 'Blog', 'Careers', 'Press'] },
      { title: 'Resources', links: ['Help Center', 'Community', 'Contact', 'API'] },
      { title: 'Legal', links: ['Privacy', 'Terms', 'Cookies'] },
    ],
    socials: ['Twitter', 'Instagram', 'LinkedIn'],
  },
  // ── Iter 138 · Agency Long-Scroll ────────────────────────────────────
  agency_glass_stat: {
    eyebrow: 'Trusted Since 2003',
    title: 'Your local experts — answering 24/7',
    tagline: 'Family-owned, fully insured, and rooted in the community.',
    image_url: 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1600&q=80',
    stats: [
      { value: '20+',    label: 'Years Serving' },
      { value: '5,000+', label: 'Happy Clients' },
      { value: '24/7',   label: 'Emergency Calls' },
    ],
    buttons: [
      { text: 'Call Now',     link: 'tel:+15550001234' },
      { text: 'Get a Quote',  link: '#contact' },
    ],
    accent: '#F97316',
  },
  agency_service_cards: {
    eyebrow: 'What we do',
    title: 'Services that solve the problem fast',
    tagline: 'Same-day appointments. Up-front pricing. Guaranteed work.',
    items: [
      { title: 'Emergency Repair',          description: 'Same-day service, 24/7 hotline.',                    image: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=900&q=80' },
      { title: 'Installation',              description: 'New systems with manufacturer warranty.',           image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=900&q=80' },
      { title: 'Inspection & Maintenance',  description: 'Annual tune-ups, transparent pricing.',             image: 'https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=900&q=80' },
    ],
    link_text: 'View all services',
    link: '#services',
    accent: '#F97316',
  },
  agency_cta_band: {
    eyebrow: 'Emergency hotline',
    title: '24-hour emergency? We answer.',
    tagline: 'A live technician will be at your door within the hour.',
    phone: '(555) 200-7777',
    note: 'No call-out fees · Family-owned since 2003',
    accent: '#F97316',
  },
  agency_why_grid: {
    eyebrow: 'Why Choose Us',
    title: 'Why homeowners keep calling back',
    tagline: 'Six promises we put in writing on every job.',
    items: [
      { icon: '⚡', title: 'Fast Response',     description: 'On-site within 60 minutes inside city limits.' },
      { icon: '🛠️', title: 'Licensed Pros',    description: 'Every tech is bonded, insured & background-checked.' },
      { icon: '💰', title: 'Upfront Pricing',  description: 'No surprises — flat rates quoted before we start.' },
      { icon: '⭐', title: '5-Star Reviewed',  description: 'Hundreds of verified Google reviews back our work.' },
      { icon: '🔧', title: 'Lifetime Warranty', description: 'On parts & labor for all major installations.' },
      { icon: '🌱', title: 'Eco-Friendly',     description: 'Modern, efficient systems that lower your bills.' },
    ],
    accent: '#F97316',
  },
  agency_stat_quad: {
    title: '',
    stats: [
      { value: '20+',    label: 'Years in business' },
      { value: '15K+',   label: 'Jobs completed' },
      { value: '4.9★',   label: 'Average rating' },
      { value: '60 min', label: 'Avg response time' },
    ],
    bg_dark: true,
    accent: '#F97316',
  },
  agency_review_badge: {
    platform: 'Google',
    rating: '4.9',
    count: '432',
    cta: 'Read all reviews',
    link: '#reviews',
    accent: '#F97316',
  },
};
